import { cloneDeep } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Carousel, CarouselControl, CarouselItem } from 'reactstrap';
import { Stepper } from '../../../../../../common/components/CardStepper/Stepper';
import { useCommonWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { PathGraphContext } from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { Entry, useEntryCRUDActions } from '@mm/state/actions/EntryCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import ButtonModel, { ButtonType } from '@mm/state/model/ButtonModel';
import WidgetModel, {
  GalleryWidgetConfig,
  ListAndGalleryEntry,
  WidgetBase,
} from '@mm/state/model/WidgetModel';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import ConfirmDeleteModal from '../../../../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import { CONFIRM_PAGE_DELETE_WIDGET } from '../../../../../pages/ChatbotEditorPage/constants';
import ListItem from '../../../requestModels/widgets/ListWidget/ListItem';
import { ItemsContainer } from '../common/sorting';
import { useSorting } from '../common/sortingHook';
import { getWidgetErrors } from '../makeWidget/get-widget-errors';
import ListWidgetItemComponent from '../widgetItem/ListItem/ListItem';
import { MAX_GALLERY_ITEMS } from './constants';
import './Gallery.scss';

const ANIMATION_DURATION = 1000;

type Props = {
  widget: WidgetBase<GalleryWidgetConfig> & Partial<WidgetModel>;
  setUnsavedEntryButton?: (unsavedButton: ButtonModel | undefined) => void;
  disabledButtonTypes?: ButtonType[];
  showAttributePicker?: boolean;
  usedInSlideout?: boolean;
  forInstagram?: boolean;
};

export const Gallery = (props: Props) => {
  const {
    widget,
    setUnsavedEntryButton,
    disabledButtonTypes,
    showAttributePicker = true,
    usedInSlideout,
    forInstagram,
  } = props;

  const {
    config: { entries },
    id: widgetId,
  } = widget;

  const pageId = useSelector(activeFacebookPageIdSelector);

  const { update: onWidgetUpdated } = useCommonWidgetHandlers();

  const attributes = useSelector(selectAttributes);

  const {
    onSortEnd,
    setItemsContainerRef,
    setItemRef,
    getSortedEntries,
    setSortedEntries,
  } = useSorting({ widgetId, entries });

  const [activeIndex, setActiveIndex] = useState(0);
  const [previousActiveIndex, setPreviousActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [deletedEntries, setDeletedEntries] = useState<number[]>([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ListAndGalleryEntry | null>(
    null,
  );

  const { delete: deleteWidget } = useWidgetCRUDActions();

  const {
    create: createEntry,
    delete: deleteEntry,
    update: updateEntry,
  } = useEntryCRUDActions();

  const { deselectWidgetButtons, removeNodeAndSuccessors } =
    useContext(PathGraphContext);

  const onUpdateEntry = (entry: Parameters<typeof updateEntry>[1]) => {
    updateEntry(widget.id, entry);
  };

  const onCreateItem = () => {
    const maxSortOrder = Math.max(...entries.map(entry => entry.sortOrder), 0);

    const newListItem = ListItem.make(
      pageId,
      {
        title: 'Title',
        description: '',
        url: '',
        uploadId: '',
        assetId: '',
      },
      maxSortOrder + 1,
    );

    return createEntry(widget.id, newListItem);
  };

  const toggleShowConfirmDelete = (selectedItem: Entry) => {
    setShowConfirmDelete(!showConfirmDelete);
    setSelectedItem((selectedItem as ListAndGalleryEntry) || null);
  };

  const setIndex = (value: number) => {
    setPreviousActiveIndex(activeIndex);
    setActiveIndex(value);
  };

  const getNextIndex = () =>
    activeIndex === items.length - 1 ? 0 : activeIndex + 1;

  const getPrevIndex = () =>
    activeIndex === 0 ? items.length - 1 : activeIndex - 1;

  const next = () => {
    if (animating) return;
    setIndex(getNextIndex());
  };

  const previous = () => {
    if (animating) return;
    setIndex(getPrevIndex());
  };

  const deselectActiveButtonInPath = useCallback(() => {
    const allWidgetButtonsArray =
      widget.config.entries?.map(e => e.buttons) || ([[]] as ButtonModel[][]);
    const allWidgetButtons = ([] as ButtonModel[]).concat(
      ...allWidgetButtonsArray,
    );
    deselectWidgetButtons?.(allWidgetButtons);
  }, [widget, deselectWidgetButtons]);

  const goToIndex = useCallback(
    newIndex => {
      if (animating) return;
      setIndex(newIndex);
    },
    [animating],
  );

  setSortedEntries(entries);
  const items = getSortedEntries();

  const memoisedItems = useMemo(() => items, [items]);
  const stepperItems = useMemo(
    () =>
      items.filter(
        (entry: ListAndGalleryEntry) => deletedEntries.indexOf(entry.id) === -1,
      ),
    [items, deletedEntries],
  );

  const createNewItem = async () => {
    if (animating) return;
    await onCreateItem();
    deselectActiveButtonInPath();
    setIndex(items.length);
  };

  const onDeleteEntryLocal = (item: Entry) => () => {
    const configEntries = widget.config.entries || [];
    if (configEntries.length === 1) {
      toggleShowConfirmDelete(item);
    } else {
      deselectActiveButtonInPath();
      const entryButtons = item.buttons;
      entryButtons.forEach(b =>
        removeNodeAndSuccessors?.({
          id: b.id,
          type: 'button',
          actionType: 'delete',
          parentId: widget.id,
          parentType: 'widget',
        }),
      );
      deleteEntry(widget.id, item);
    }
  };

  const onDeleteItemHandler = (item: Entry, index: number) => () => {
    if (animating) return;
    if (index > 0) {
      setIndex(index - 1);
      setDeletedEntries([...deletedEntries, item.id]);
      setTimeout(() => {
        onDeleteEntryLocal(item)();
      }, ANIMATION_DURATION);
    } else {
      onDeleteEntryLocal(item)();
      setIndex(0);
    }
  };

  const onOrderButtonClick = async (forward: boolean) => {
    const nextIndex = forward ? getNextIndex() : getPrevIndex();

    const sortedItems = getSortedEntries();
    const fromEntry = sortedItems[activeIndex];
    const toEntry = sortedItems[nextIndex];

    onUpdateEntry({
      ...fromEntry,
      sortOrder: nextIndex + 1,
    });

    onUpdateEntry({
      ...toEntry,
      sortOrder: activeIndex + 1,
    });

    // optimistic switch
    goToIndex(nextIndex);
  };

  const onRatioChange = (isImageSquare?: boolean) => {
    const updatedWidget = cloneDeep(widget);
    updatedWidget.config.imageAspectRatio = isImageSquare
      ? 'square'
      : 'horizontal';
    onWidgetUpdated(updatedWidget as WidgetModel);
  };

  useEffect(() => {
    if (widget.config.entries.length === 0) {
      onCreateItem();
    }
  }, []);

  const entriesErrors = useMemo(
    () =>
      getWidgetErrors(widget as WidgetModel, widget.validated.facebook).entries,
    [widget],
  );

  const isRatioSelected =
    memoisedItems.findIndex((item: ListAndGalleryEntry) => !!item.file) > -1;

  const onDeleteWidget = () => {
    if (selectedItem) {
      onDeleteEntryLocal(selectedItem);

      removeNodeAndSuccessors?.({
        id: widget.id,
        type: 'widget',
        actionType: 'delete',
        parentType: 'dialogue',
        parentId: widget.pageId,
      });
      deleteWidget(widget as WidgetModel);
    }
  };

  const slides = memoisedItems.map((item, index) => {
    return (
      <CarouselItem
        className="custom-tag"
        tag="div"
        key={item.id}
        onExited={() => {
          if (previousActiveIndex > activeIndex) {
            setTimeout(() => {
              setAnimating(false);
              window.dispatchEvent(new Event('synthetic-resize'));
            }, 0);
          }
        }}
        onEntered={() => {
          if (previousActiveIndex <= activeIndex) {
            setAnimating(false);
            setTimeout(() => {
              window.dispatchEvent(new Event('synthetic-resize'));
            }, 0);
          }
        }}
        onExiting={() => {
          setAnimating(true);
        }}
        onEntering={() => {
          setAnimating(true);
        }}
      >
        <ListWidgetItemComponent
          {...props}
          key={item.id}
          disabledButtonTypes={disabledButtonTypes}
          index={index}
          itemRef={setItemRef}
          item={item}
          widget={widget}
          onUpdate={onUpdateEntry}
          onDelete={onDeleteItemHandler(item, index)}
          onOrderButtonClick={onOrderButtonClick}
          attributes={attributes}
          shouldCroppedImmediately={true}
          onRatioChange={onRatioChange}
          couldShowUploadFormModal={!isRatioSelected}
          error={entriesErrors[index]}
          setUnsavedEntryButton={setUnsavedEntryButton}
          showAttributePicker={showAttributePicker}
          usedInSlideout={usedInSlideout}
          forInstagram={forInstagram}
        />
      </CarouselItem>
    );
  });

  const canAddMoreItems = memoisedItems.length < MAX_GALLERY_ITEMS;

  return (
    <div className="gallery-widget">
      <ConfirmDeleteModal
        headerText={CONFIRM_PAGE_DELETE_WIDGET}
        show={showConfirmDelete}
        onClose={toggleShowConfirmDelete}
        onDelete={onDeleteWidget}
      />
      <ItemsContainer
        className="gallery-widget__items-container"
        containerRef={setItemsContainerRef}
        lockAxis="y"
        useDragHandle={true}
        lockToContainerEdges={true}
        lockOffset="90%"
        onSortEnd={onSortEnd}
      >
        <Stepper
          currentIndex={activeIndex}
          totalAmount={stepperItems.length}
          onCardSelected={(newIndex: any) => {
            goToIndex(newIndex);
            deselectActiveButtonInPath();
          }}
          onAddNewCard={createNewItem}
          showAddIcon={canAddMoreItems}
        />
        <Carousel
          className={
            'gallery-widget__carousel' +
            (animating ? ' gallery-widget__carousel--animating' : '')
          }
          activeIndex={activeIndex}
          keyboard={false}
          next={next}
          previous={previous}
          interval={false}
        >
          {slides}
          <CarouselControl
            className="gallery-widget__carousel-button--left"
            direction="prev"
            directionText="Previous"
            onClickHandler={previous}
          />
          <CarouselControl
            className="gallery-widget__carousel-button--right"
            direction="next"
            directionText="Next"
            onClickHandler={next}
          />
        </Carousel>
      </ItemsContainer>
    </div>
  );
};
